import { BrowserModule } from '@angular/platform-browser';
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule } from '@angular/core';
import { LoadingChunkErrorHandler } from './libs/utils/loading-chunk-error-handler';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { environment } from '../environments/environment';
import { AppFirebaseModule } from './app-firebase.module';
import { ServiceModule, VersionCheckServiceModule } from '@msg91/service';
import { ServicesPermissionModule } from '@msg91/services/permission';
import { ServicesMsg91SegmentoModule } from '@msg91/services/msg91/segmento/segmento';
import { ServicesMsg91ComposeModule } from '@msg91/services/msg91/compose';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { StoreModule } from '@ngrx/store';
import { EffectModule, reducers } from './ngrx';
import { extModules } from './build-specifics';
import { loginsReducer } from './login/ngrx/store/login.state';
import { LogInEffectsModule } from './login/ngrx/effects/login-effects.module';
import { BotBaseUrlToken, HelloBaseUrlToken, Msg91AgenticAiChatbotUrl, ProxyBaseUrls } from '@msg91/models/root-models';
import { ServicesHelloModule } from '@msg91/services/hello';
import { ServicesMsg91WhatsappModule } from '@msg91/services/msg91/whatsapp';
import { ServicesMsg91HelloOneInboxModule } from '@msg91/services/msg91/hello';
import { ServicesMsg91IndexDbModule } from '@msg91/services/msg91/indexDB';
import { HELLO_MESSAGE_INDEX_DB_KEY } from '@msg91/constant';
import {
    MSG91_ENABLE_MSG91_PROXY,
    MSG91_HELLO_AGENTIC_AI_CHATBOT_URL,
    MSG91_HELLO_BOT_COMPANY_ID_API_URL,
    MSG91_HELLO_BOT_DEFAULT_MESSAGE_URL,
    MSG91_HELLO_BOT_EXAMPLE_API_URL,
    MSG91_HELLO_BOT_PROMPT_API_URL,
    MSG91_SHOW_VIASOCKET_FLOWS,
    MSG91_VIASOCKET_BASE_URL,
    MSG91_VIASOCKET_ORIGIN_URL,
} from './constants';
import { clearStateMetaReducer } from './ngrx/store/app.state';
import { ServicesAdminEmailEmailModule } from '@msg91/services/admin/email/email';
import { ServicesAdminRootModule } from '@msg91/services/admin/root';
import { ServicesHttpWrapperModule } from '@msg91/services/httpWrapper';
import { ServicesAdminAuthModule } from '@msg91/services/admin/auth';
import {
    AdminErrorInterceptor,
    ServicesAdminErrorInterceptorModule,
} from '@msg91/services/interceptor/adminErrorInterceptor';
import { UiPrimeNgToastModule } from '@msg91/ui/prime-ng-toast';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatDialogModule } from '@angular/material/dialog';
import { ENVIRONMENT_TOKEN } from '@msg91/constant';
import { provideNativeDateAdapter } from '@angular/material/core';
import {
    NgxMatNativeDateModule,
    NgxMatDatepickerActions,
    NgxMatDatepickerApply,
    NgxMatDatepickerCancel,
    NgxMatDatepickerClear,
    NgxMatDatepickerInput,
    NgxMatDatetimepicker,
} from '@ngxmc/datetime-picker';

const envModule = environment.production ? [] : extModules;

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        AppFirebaseModule,
        StoreModule.forRoot(reducers, { metaReducers: [clearStateMetaReducer] }),
        ...envModule,
        EffectModule,
        StoreModule.forFeature('auth', loginsReducer),
        LogInEffectsModule,
        ServiceModule.forRoot(),
        VersionCheckServiceModule,
        ServicesPermissionModule,
        ServicesMsg91SegmentoModule,
        ServicesMsg91ComposeModule,
        ServicesHelloModule,
        ServicesMsg91WhatsappModule,
        ServicesMsg91HelloOneInboxModule,
        ServicesAdminEmailEmailModule,
        ServicesAdminRootModule,
        ServicesHttpWrapperModule,
        ServicesAdminAuthModule,
        ServicesAdminErrorInterceptorModule,
        UiPrimeNgToastModule,
        ScrollingModule,
        MatDialogModule,
        ServicesMsg91IndexDbModule.forRoot({
            name: 'hello_message_store',
            version: 1,
            objectStoresMeta: [
                {
                    store: HELLO_MESSAGE_INDEX_DB_KEY,
                    storeConfig: { autoIncrement: false },
                },
            ],
            expireIt: new Date(2049, 12, 31),
        }),
        NgxMatNativeDateModule,
        NgxMatDatepickerActions,
        NgxMatDatepickerApply,
        NgxMatDatepickerCancel,
        NgxMatDatepickerClear,
        NgxMatDatepickerInput,
        NgxMatDatetimepicker,
    ],
    providers: [
        { provide: ProxyBaseUrls.Env, useValue: environment.env },
        { provide: ProxyBaseUrls.ProxyURL, useValue: null },
        { provide: ProxyBaseUrls.BaseServer, useValue: environment.server },
        { provide: ProxyBaseUrls.BaseURL, useValue: environment.adminApiServer },
        { provide: ProxyBaseUrls.FirebaseConfig, useValue: environment.firebaseConfig },
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                appearance: 'outline',
                floatLabel: 'auto',
            },
        },
        { provide: ProxyBaseUrls.EmailProxy, useValue: environment.emailServer + '' + environment.emailProxy },
        { provide: ProxyBaseUrls.HelloProxy, useValue: environment.helloServer },
        { provide: ProxyBaseUrls.RcsProxy, useValue: environment.rcsServer },
        { provide: ProxyBaseUrls.MsgProxy, useValue: environment.msgServer },
        {
            provide: ProxyBaseUrls.IToken,
            useValue: {
                token: null,
                companyId: null,
            },
        },
        {
            provide: ProxyBaseUrls.AdminServer,
            useValue: environment.adminServer,
        },
        {
            provide: ProxyBaseUrls.WhatsAppProxy,
            useValue: environment.whatsAppServer,
        },
        { provide: ProxyBaseUrls.VoiceBaseURL, useValue: environment.voiceServer + '' + environment.voiceProxy },
        { provide: ProxyBaseUrls.EmailServerURL, useValue: environment.emailServer },
        {
            provide: ProxyBaseUrls.SubscriptionProxy,
            useValue: environment.subscriptionProxy + '' + environment.emailProxy,
        },
        {
            provide: ProxyBaseUrls.SubscriptionURLProxy,
            useValue: environment.subscriptionProxy,
        },
        { provide: ProxyBaseUrls.ReportsUrl, useValue: environment.reportsUrl },
        { provide: ProxyBaseUrls.NumbersProxy, useValue: environment.numbersProxy },
        { provide: ProxyBaseUrls.InvisibleOTPProxy, useValue: environment.invisibleOTPProxy },
        { provide: ProxyBaseUrls.InvisibleOtpBaseURL, useValue: environment.invisibleOtpBaseURL },
        { provide: ProxyBaseUrls.AdminProxy, useValue: environment.adminProxy },
        { provide: ProxyBaseUrls.SegmentoBaseURL, useValue: environment.segmentoProxy },
        { provide: ProxyBaseUrls.SegmentoV1BaseURL, useValue: environment.segmentoProxy },
        { provide: ProxyBaseUrls.SegmentoMsg91BasePath, useValue: environment.segmentoProxy },
        { provide: HTTP_INTERCEPTORS, useClass: AdminErrorInterceptor, multi: true },
        { provide: ProxyBaseUrls.CampaignProxy, useValue: environment.campaignServer },
        { provide: ProxyBaseUrls.FileUploadProxy, useValue: environment.adminApiServer },
        {
            provide: MAT_DIALOG_DEFAULT_OPTIONS,
            useValue: {
                autoFocus: false,
                hasBackdrop: true,
                disableClose: true,
                restoreFocus: false,
            },
        },
        { provide: ENVIRONMENT_TOKEN, useValue: environment },
        { provide: HelloBaseUrlToken, useValue: environment.helloServer || '' },
        { provide: BotBaseUrlToken, useValue: environment.adminApiServer || '' },
        { provide: ProxyBaseUrls.HelloBaseURL, useValue: environment.helloServer || '' },
        { provide: ProxyBaseUrls.BasePath, useValue: '' },
        { provide: ProxyBaseUrls.ShowViaSocketFlows, useValue: MSG91_SHOW_VIASOCKET_FLOWS },
        { provide: ProxyBaseUrls.ViaSocketOriginUrl, useValue: MSG91_VIASOCKET_ORIGIN_URL },
        { provide: ProxyBaseUrls.EnableMsg91Proxy, useValue: MSG91_ENABLE_MSG91_PROXY },
        { provide: ProxyBaseUrls.ProdServer, useValue: environment.prodServer || '' },
        { provide: ProxyBaseUrls.HelloBotExampleApi, useValue: MSG91_HELLO_BOT_EXAMPLE_API_URL },
        { provide: ProxyBaseUrls.HelloBotPromptApi, useValue: MSG91_HELLO_BOT_PROMPT_API_URL },
        { provide: ProxyBaseUrls.HelloBotAndCompanyIdApi, useValue: MSG91_HELLO_BOT_COMPANY_ID_API_URL },
        { provide: ProxyBaseUrls.HelloDefaultMessage, useValue: MSG91_HELLO_BOT_DEFAULT_MESSAGE_URL },
        { provide: ProxyBaseUrls.ViaSocketProjectId, useValue: environment.viasocketProjectId || '' },
        { provide: ProxyBaseUrls.ViasocketBaseUrl, useValue: MSG91_VIASOCKET_BASE_URL },
        {
            provide: Msg91AgenticAiChatbotUrl,
            useValue: MSG91_HELLO_AGENTIC_AI_CHATBOT_URL,
        },
        { provide: ErrorHandler, useClass: LoadingChunkErrorHandler },
        provideHttpClient(withInterceptorsFromDi()),
        provideNativeDateAdapter(),
    ],
})
export class AppModule {}
